// frontend/src/pages/admin/AuditEnedisGlobal.jsx
//
// Page admin — Audit ENEDIS global
// Liste tous les PDLs du dashboard avec anomalies détectées par le service
// d'audit backend (/api/enedis/audit/:pdlId), triés par potentiel de récupération.
//
// Charge la flotte via le hook useOptimizablePDLs, puis fan-out limité à 10
// requêtes parallèles vers le backend d'audit. Tolère les échecs unitaires
// (Promise.allSettled) et affiche "—" pour les PDLs indisponibles.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiShield, FiAlertTriangle, FiCheckCircle, FiRefreshCw,
  FiEye, FiTrendingUp, FiActivity, FiLoader,
} from 'react-icons/fi';
import { useOptimizablePDLs } from '../../hooks/dashboard/useOptimizablePDLs';
import { fetchWithAuth } from '../../services/api';

// ─── Constantes ────────────────────────────────────────────────────────────────
const MAX_PDLS_TO_SCAN = 200;
const CONCURRENCY      = 10;
const VISIBLE_ROWS     = 50;

const SEVERITY_RANK = { error: 3, warning: 2, info: 1, ok: 0 };

// ─── Helpers ───────────────────────────────────────────────────────────────────
const fmtEur = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
};

const sevColor = (sev) => {
  if (sev === 'error')   return 'bg-red-50 text-red-700 border-red-200';
  if (sev === 'warning') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (sev === 'info')    return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
};

// Concurrence limitée : N workers consomment une queue FIFO.
async function runWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const run = async () => {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      try {
        results[i] = { status: 'fulfilled', value: await worker(items[i], i) };
      } catch (err) {
        results[i] = { status: 'rejected', reason: err };
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, run));
  return results;
}

// Mappings status finding (aligné backend & AnalyseTurpe.jsx).
const STATUS_META = {
  open:      { label: 'Ouvert',    bg: 'bg-gray-100',    text: 'text-gray-700',    border: 'border-gray-300'    },
  contested: { label: 'Contesté',  bg: 'bg-orange-100',  text: 'text-orange-700',  border: 'border-orange-300'  },
  resolved:  { label: 'Résolu',    bg: 'bg-green-100',   text: 'text-green-700',   border: 'border-green-300'   },
  refunded:  { label: 'Remboursé', bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300' },
  dismissed: { label: 'Écarté',    bg: 'bg-gray-100',    text: 'text-gray-500',    border: 'border-gray-200'    },
};
const STATUS_KEYS = ['open', 'contested', 'resolved', 'refunded', 'dismissed'];

// Normalise la réponse backend en un agrégat ligne-table.
// Le contrat backend est en cours de dev. On reste tolérant sur le shape.
function summarizeAudit(pdl, audit) {
  const findings = Array.isArray(audit?.findings) ? audit.findings : [];
  const counts   = findings.reduce(
    (acc, f) => {
      const sev = (f?.severity || 'info').toLowerCase();
      if (sev in acc) acc[sev] += 1;
      return acc;
    },
    { error: 0, warning: 0, info: 0 }
  );
  const totalAnomalies = counts.error + counts.warning;
  const maxSeverity = findings.reduce((m, f) => {
    const r = SEVERITY_RANK[(f?.severity || 'info').toLowerCase()] || 0;
    return r > m ? r : m;
  }, 0);

  // Distribution des findings par status (open|contested|resolved|refunded|dismissed).
  // Findings sans `status` (audit pas encore persisté) → "open" par défaut.
  const statusCounts = findings.reduce(
    (acc, f) => {
      const s = STATUS_KEYS.includes(f?.status) ? f.status : 'open';
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { open: 0, contested: 0, resolved: 0, refunded: 0, dismissed: 0 }
  );

  // Top finding = finding au plus gros impact annuel (sinon premier error/warning).
  const sortedByImpact = [...findings].sort(
    (a, b) => Number(b?.risque_annuel_eur || 0) - Number(a?.risque_annuel_eur || 0)
  );
  const top = sortedByImpact[0] || null;

  return {
    pdl_id:         pdl.pdl_id,
    pdl_number:     pdl.pdl_number,
    site_name:      pdl.site_name,
    company_name:   pdl.company_name,
    commercial_name: pdl.commercial_name,
    opportunity_id: pdl.opportunity_id,
    account_id:     pdl.account_id,

    audit_loaded:   audit !== null,
    audit_failed:   audit === undefined,
    findings_count: totalAnomalies,
    findings,
    counts,
    status_counts:  statusCounts,
    max_severity:   maxSeverity, // 0 ok / 1 info / 2 warn / 3 error
    risque_annuel_eur: Number(audit?.risque_annuel_eur ?? 0),
    retroactif_eur:    Number(audit?.retroactif_eur ?? 0),
    top_finding:    top,
  };
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, hint, tone = 'slate' }) => {
  const tones = {
    slate:   'bg-white border-gray-200',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    blue:    'bg-blue-50 border-blue-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || tones.slate} flex flex-col gap-1`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        {icon}<span>{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
      {hint && <div className="text-[9px] text-gray-500">{hint}</div>}
    </div>
  );
};

// ─── Badge Score ──────────────────────────────────────────────────────────────
const ScoreBadge = ({ row }) => {
  if (row.audit_failed) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-gray-50 text-gray-500 border-gray-200">
        —
      </span>
    );
  }
  if (row.findings_count === 0) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${sevColor('ok')}`}>
        <FiCheckCircle className="w-2.5 h-2.5" /> OK
      </span>
    );
  }
  const sevName = row.max_severity === 3 ? 'error' : row.max_severity === 2 ? 'warning' : 'info';
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${sevColor(sevName)}`}>
      <FiAlertTriangle className="w-2.5 h-2.5" />
      {row.findings_count} anomalie{row.findings_count > 1 ? 's' : ''}
    </span>
  );
};

// ─── Status global cell ──────────────────────────────────────────────────────
// Affiche le résumé des findings par status (ex. "2 ouverts / 1 contesté / 1 résolu").
const StatusGlobalCell = ({ row }) => {
  if (row.audit_failed) return <span className="text-gray-400 italic text-[10px]">—</span>;
  if (!row.findings || row.findings.length === 0) {
    return <span className="text-[10px] text-emerald-600">—</span>;
  }
  // On ne montre que les statuts non vides, dans un ordre lisible.
  const parts = STATUS_KEYS
    .map((s) => ({ key: s, count: row.status_counts?.[s] || 0 }))
    .filter((p) => p.count > 0);

  if (parts.length === 0) return <span className="text-[10px] text-gray-400">—</span>;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {parts.map((p) => {
        const meta = STATUS_META[p.key];
        return (
          <span
            key={p.key}
            className={`inline-flex items-center px-1 py-0 rounded text-[9px] font-semibold border ${meta.bg} ${meta.text} ${meta.border}`}
          >
            {p.count} {meta.label.toLowerCase()}
          </span>
        );
      })}
    </div>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const TableSkeleton = () => (
  <div className="p-4 space-y-2 animate-pulse">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-6 bg-gray-100 rounded" />
    ))}
  </div>
);

// ─── Page principale ─────────────────────────────────────────────────────────
export default function AuditEnedisGlobal() {
  const navigate = useNavigate();
  const { data: pdls, loading: pdlsLoading, error: pdlsError, refetch: refetchPdls } = useOptimizablePDLs();

  // Cache local des résultats d'audit : pdl_id → audit summary
  const [auditRows, setAuditRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0 });

  // Filtres
  const [severityMin, setSeverityMin] = useState('all'); // all | warning | error
  const [commercialFilter, setCommercialFilter] = useState('all');
  const [hideOk, setHideOk] = useState(true);
  // Status finding (multi-select). Vide = aucun filtre (toutes les lignes passent).
  const [statusFilter, setStatusFilter] = useState([]);

  const toggleStatusFilter = (key) => {
    setStatusFilter((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  };

  // ─── Scan des audits (fan-out limité) ─────────────────────────────────────
  const runScan = useCallback(async (pdlList) => {
    if (!pdlList || pdlList.length === 0) {
      setAuditRows([]);
      return;
    }
    const subset = pdlList.slice(0, MAX_PDLS_TO_SCAN);
    setScanning(true);
    setScanProgress({ done: 0, total: subset.length });

    let done = 0;
    const settled = await runWithConcurrency(subset, CONCURRENCY, async (pdl) => {
      try {
        const json = await fetchWithAuth(`/api/enedis/audit/${pdl.pdl_id}`);
        // Le backend renvoie soit { data: {...} } soit l'objet direct
        const audit = json?.data ?? json;
        return summarizeAudit(pdl, audit || {});
      } catch (e) {
        // On marque ce PDL comme "audit_failed: true" pour afficher "—" en UI
        return summarizeAudit(pdl, undefined);
      } finally {
        done += 1;
        setScanProgress({ done, total: subset.length });
      }
    });

    // settled[i] est toujours fulfilled car worker catch les erreurs (et renvoie une row "failed").
    const rows = settled.map((s) => s.value).filter(Boolean);
    setAuditRows(rows);
    setScanning(false);
  }, []);

  // Trigger initial du scan dès que les PDLs sont chargés
  useEffect(() => {
    if (!pdlsLoading && Array.isArray(pdls) && pdls.length > 0) {
      runScan(pdls);
    } else if (!pdlsLoading && Array.isArray(pdls) && pdls.length === 0) {
      setAuditRows([]);
    }
  }, [pdlsLoading, pdls, runScan]);

  // ─── Liste des commerciaux pour le filtre ─────────────────────────────────
  const commercials = useMemo(() => {
    const set = new Set();
    auditRows.forEach((r) => {
      if (r.commercial_name) set.add(r.commercial_name);
    });
    return ['all', ...Array.from(set).sort()];
  }, [auditRows]);

  // ─── Filtrage + tri ───────────────────────────────────────────────────────
  const filteredRows = useMemo(() => {
    let rows = [...auditRows];

    if (severityMin === 'warning') {
      rows = rows.filter((r) => r.max_severity >= 2);
    } else if (severityMin === 'error') {
      rows = rows.filter((r) => r.max_severity >= 3);
    }

    if (commercialFilter !== 'all') {
      rows = rows.filter((r) => r.commercial_name === commercialFilter);
    }

    if (hideOk) {
      rows = rows.filter((r) => r.findings_count > 0 || r.audit_failed);
    }

    // Filtre status (multi). On garde une ligne si AU MOINS un finding matche l'un des statuts sélectionnés.
    if (statusFilter.length > 0) {
      rows = rows.filter((r) =>
        statusFilter.some((s) => (r.status_counts?.[s] || 0) > 0)
      );
    }

    rows.sort((a, b) => b.risque_annuel_eur - a.risque_annuel_eur);
    return rows;
  }, [auditRows, severityMin, commercialFilter, hideOk, statusFilter]);

  const visibleRows = useMemo(() => filteredRows.slice(0, VISIBLE_ROWS), [filteredRows]);

  // ─── KPIs ─────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const totalScanned = auditRows.length;
    const totalAnomalies = auditRows.reduce((s, r) => s + r.findings_count, 0);
    const cumulAnnuel = auditRows.reduce((s, r) => s + (Number(r.risque_annuel_eur) || 0), 0);
    const cumulRetro  = auditRows.reduce((s, r) => s + (Number(r.retroactif_eur)    || 0), 0);
    return { totalScanned, totalAnomalies, cumulAnnuel, cumulRetro };
  }, [auditRows]);

  const handleViewDetail = (row) => {
    navigate(`/dashboard/admin/analyse-turpe/${row.pdl_id}?tab=audit`);
  };

  const handleRefresh = () => {
    refetchPdls();
  };

  const fleetHasNoAnomalies =
    !scanning &&
    !pdlsLoading &&
    auditRows.length > 0 &&
    auditRows.every((r) => r.findings_count === 0 && !r.audit_failed);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="w-full max-w-none space-y-4">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FiShield className="text-blue-600" />
            Audit ENEDIS — vue globale
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Anomalies détectées sur l'ensemble de la flotte (TURPE, dépassements, puissance souscrite,
            courbe de charge…). Triées par potentiel de récupération annuel.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={scanning || pdlsLoading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border border-gray-200 bg-white rounded hover:bg-gray-50 disabled:opacity-50"
        >
          <FiRefreshCw className={`w-3 h-3 ${scanning ? 'animate-spin' : ''}`} />
          Re-scanner
        </button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <KpiCard
          icon={<FiActivity className="w-3 h-3" />}
          label="PDLs scannés"
          value={kpis.totalScanned}
          hint={scanning ? `${scanProgress.done}/${scanProgress.total} en cours…` : 'Total scanné'}
          tone="blue"
        />
        <KpiCard
          icon={<FiAlertTriangle className="w-3 h-3" />}
          label="Anomalies détectées"
          value={kpis.totalAnomalies}
          hint="Toutes sévérités (warn + error)"
          tone="amber"
        />
        <KpiCard
          icon={<FiTrendingUp className="w-3 h-3" />}
          label="Potentiel annuel cumulé"
          value={fmtEur(kpis.cumulAnnuel)}
          hint="€/an récupérables"
          tone="emerald"
        />
        <KpiCard
          icon={<FiTrendingUp className="w-3 h-3" />}
          label="Rétroactif 6 ans cumulé"
          value={fmtEur(kpis.cumulRetro)}
          hint="Réclamable sur 6 ans"
          tone="emerald"
        />
      </div>

      {/* Erreurs PDL list */}
      {pdlsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Erreur de chargement de la flotte PDL. <button onClick={refetchPdls} className="underline">Réessayer</button>
        </div>
      )}

      {/* Tableau */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">

        {/* Filtres */}
        <div className="px-3 py-2 border-b border-gray-200 flex flex-wrap items-center gap-2 bg-gray-50">
          <div className="flex items-center gap-1">
            <label htmlFor="audit-sev" className="text-[10px] text-gray-500 uppercase tracking-wider">Sévérité min.</label>
            <select
              id="audit-sev"
              value={severityMin}
              onChange={(e) => setSeverityMin(e.target.value)}
              className="py-0.5 px-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white"
            >
              <option value="all">Toutes</option>
              <option value="warning">≥ Warning</option>
              <option value="error">Erreur uniquement</option>
            </select>
          </div>

          <div className="flex items-center gap-1">
            <label htmlFor="audit-comm" className="text-[10px] text-gray-500 uppercase tracking-wider">Commercial</label>
            <select
              id="audit-comm"
              value={commercialFilter}
              onChange={(e) => setCommercialFilter(e.target.value)}
              className="py-0.5 px-1 border border-gray-200 rounded text-[10px] focus:outline-none focus:ring-1 focus:ring-blue-300 bg-white max-w-[160px]"
            >
              {commercials.map((c) => (
                <option key={c} value={c}>{c === 'all' ? 'Tous' : c}</option>
              ))}
            </select>
          </div>

          <label className="inline-flex items-center gap-1 text-[10px] text-gray-700 cursor-pointer ml-2">
            <input
              type="checkbox"
              checked={hideOk}
              onChange={(e) => setHideOk(e.target.checked)}
              className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-300"
            />
            Cacher PDLs sans anomalie
          </label>

          {/* Filtre status finding (multi-select via chips toggle). */}
          <div className="flex items-center gap-1 ml-2 flex-wrap">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Status</span>
            {STATUS_KEYS.map((s) => {
              const meta = STATUS_META[s];
              const active = statusFilter.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleStatusFilter(s)}
                  className={`px-1.5 py-0 rounded text-[9px] font-semibold border transition ${
                    active
                      ? `${meta.bg} ${meta.text} ${meta.border}`
                      : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
                  title={`Filtrer findings ${meta.label.toLowerCase()}`}
                >
                  {meta.label}
                </button>
              );
            })}
            {statusFilter.length > 0 && (
              <button
                type="button"
                onClick={() => setStatusFilter([])}
                className="text-[9px] text-gray-500 underline hover:text-gray-700 ml-1"
              >
                reset
              </button>
            )}
          </div>

          <div className="ml-auto text-[10px] text-gray-500">
            {scanning ? (
              <span className="inline-flex items-center gap-1">
                <FiLoader className="w-3 h-3 animate-spin" />
                Scan en cours… {scanProgress.done}/{scanProgress.total}
              </span>
            ) : (
              <span>
                {filteredRows.length} ligne{filteredRows.length > 1 ? 's' : ''}
                {filteredRows.length > VISIBLE_ROWS && ` · top ${VISIBLE_ROWS} affichées`}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        {(pdlsLoading || (scanning && auditRows.length === 0)) ? (
          <TableSkeleton />
        ) : fleetHasNoAnomalies ? (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-emerald-700 font-semibold">
              🎉 Aucune anomalie détectée sur l'ensemble de la flotte
            </p>
            <p className="text-[10px] text-gray-500 mt-1">
              {kpis.totalScanned} PDL{kpis.totalScanned > 1 ? 's' : ''} audité{kpis.totalScanned > 1 ? 's' : ''}.
            </p>
          </div>
        ) : visibleRows.length === 0 ? (
          <p className="px-3 py-6 text-center text-xs text-gray-500">
            Aucun PDL ne correspond aux critères sélectionnés.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Client / Site</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">PDL</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Commercial</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Score</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Status global</th>
                  <th className="px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">Risque /an</th>
                  <th className="px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">Rétro 6 ans</th>
                  <th className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-600">Top finding</th>
                  <th className="px-2 py-1.5 text-right text-[10px] font-semibold uppercase tracking-wider text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-[11px]">
                {visibleRows.map((row) => (
                  <tr key={row.pdl_id} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5">
                      <div className="font-semibold text-gray-900 leading-tight">{row.company_name || '—'}</div>
                      <div className="text-[10px] text-gray-500 italic">{row.site_name || '—'}</div>
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 font-mono text-[10px]">{row.pdl_number || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-700">{row.commercial_name || 'Non assigné'}</td>
                    <td className="px-2 py-1.5"><ScoreBadge row={row} /></td>
                    <td className="px-2 py-1.5"><StatusGlobalCell row={row} /></td>
                    <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                      {row.audit_failed ? '—' : fmtEur(row.risque_annuel_eur)}
                    </td>
                    <td className="px-2 py-1.5 text-right text-gray-700">
                      {row.audit_failed ? '—' : fmtEur(row.retroactif_eur)}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {row.audit_failed
                        ? <span className="text-gray-400 italic">audit indisponible</span>
                        : row.top_finding
                          ? (row.top_finding.title || row.top_finding.label || row.top_finding.code || '—')
                          : <span className="text-emerald-600">RAS</span>}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => handleViewDetail(row)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[10px] font-medium"
                      >
                        <FiEye className="w-2.5 h-2.5" />
                        Voir audit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer / limites */}
      {pdls && pdls.length > MAX_PDLS_TO_SCAN && (
        <p className="text-[10px] text-gray-400 italic">
          Limite scan : {MAX_PDLS_TO_SCAN} premiers PDLs sur {pdls.length} disponibles.
          Affiner la sélection via le filtre commercial pour un audit complet.
        </p>
      )}
    </div>
  );
}
